/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes, Slot } from '@adobe-commerce/elsie/lib';
import '@/auth/components/SuccessNotificationForm/SuccessNotificationForm.css';
import { Button } from '@adobe-commerce/elsie/components';
import { SuccessNotificationFormProps } from '@/auth/types';
import { useText } from '@adobe-commerce/elsie/i18n';
import { revokeCustomerToken } from '@/auth/api';

export const SuccessNotificationForm: FunctionComponent<
  SuccessNotificationFormProps & HTMLAttributes<HTMLDivElement>
> = ({ formSize = 'default', className = '', slots, labels }) => {
  const translations = useText({
    headingText: 'Auth.SuccessNotification.headingText',
    messageText: 'Auth.SuccessNotification.messageText',
    primaryButtonText: 'Auth.SuccessNotification.primaryButtonText',
    secondaryButtonText: 'Auth.SuccessNotification.secondaryButtonText',
  });

  return (
    <div
      className={classes([
        'auth-success-notification-form',
        `auth-success-notification-form--${formSize}`,
        className,
      ])}
      id="welcome-message"
      role="alert"
      aria-live="assertive"
      aria-labelledby={labels?.headingText ?? translations.headingText}
      aria-describedby={labels?.messageText ?? translations.messageText}
      tab-index="-1"
    >
      <p
        id="welcome-heading"
        className="auth-success-notification-form__title"
        data-testid="notification-title"
      >
        {labels?.headingText ?? translations.headingText}
      </p>
      <p
        id="welcome-details"
        className="auth-success-notification-form__content-text"
        data-testid="notification-content"
      >
        {labels?.messageText ?? translations.messageText}
      </p>
      {slots?.SuccessNotificationActions ? (
        <Slot
          data-testid="successNotificationActions"
          name="SuccessNotificationActions"
          slot={slots?.SuccessNotificationActions}
          context={{}}
        />
      ) : (
        <>
          <Button
            data-testid="primaryButton"
            type="button"
            className="auth-success-notification-form__button auth-success-notification-form__button--top"
            onClick={() => (window.location.href = '/')}
          >
            {translations.primaryButtonText}
          </Button>
          <Button
            data-testid="secondaryButton"
            type="button"
            variant="tertiary"
            onClick={async () => {
              await revokeCustomerToken();
              window.location.href = '/';
            }}
          >
            {translations.secondaryButtonText}
          </Button>
        </>
      )}
    </div>
  );
};
